import { Component, computed, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { CompanyFamilyService, FamilyContext } from '../../../../../services/companies/company-family.service';
import { TextNormalizeDirective } from '../../../../../directives/text-normalize.directive';
import { LutService } from '../../../../../services/lut.service';
import { FamilyCompanyStatus } from '../../../../../models/familyCompanyStatus';
import { FamilyGeneration } from '../../../../../models/familyGeneration';
import { BoardMemberOccupation } from '../../../../../models/boardMemberOccupation';
import {
  FamilyResponseDto,
  FamilyBoardMemberDto,
  FamilyExecutiveMemberDto,
  FamilyLargeShareholderDto,
} from '../../../../../models/api/companyFamilyDto';
import {
  FamilyUpsertRequest,
  FamilyBoardMemberUpsertDto,
  FamilyExecutiveMemberUpsertDto,
  FamilyLargeShareholderUpsertDto,
} from '../../../../../models/api/companyFamilyRequest';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { MEMBERS_DROPPED_MESSAGE, membersWereDropped } from '../../../../../models/api/yearlyResource';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

@Component({
  selector: 'app-family-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    TextNormalizeDirective,
    ReadOnlyTabDirective,
  ],
  templateUrl: './family-tab.component.html',
  styleUrl: './family-tab.component.scss'
})
export class FamilyTabComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private familyService = inject(CompanyFamilyService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private lutService = inject(LutService);
  private currentUserState = inject(CurrentUserState);
  private destroy$ = new Subject<void>();

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Family));

  familyStatuses: FamilyCompanyStatus[] = [];
  familyGenerations: FamilyGeneration[] = [];
  occupations: BoardMemberOccupation[] = [];
  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  isLargeShareholdersCarriedForward = false;
  isFamilyDetailsCarriedForward = false;
  companyBoardType: number = 0;

  boardDataSource = new MatTableDataSource<FamilyBoardMemberDto>([]);
  managementDataSource = new MatTableDataSource<FamilyBoardMemberDto>([]);
  executiveDataSource = new MatTableDataSource<FamilyExecutiveMemberDto>([]);
  shareholderDataSource = new MatTableDataSource<FamilyLargeShareholderDto>([]);

  @ViewChild('boardSort') boardSort!: MatSort;
  @ViewChild('managementSort') managementSort!: MatSort;
  @ViewChild('executiveSort') executiveSort!: MatSort;
  @ViewChild('shareholderSort') shareholderSort!: MatSort;

  boardDisplayedColumns: string[] = [
    'index', 'name', 'percentage', 'classification', 'occupation', 'familyStatus', 'generation'
  ];

  executiveDisplayedColumns: string[] = [
    'index', 'name', 'percentage', 'occupation', 'familyStatus', 'generation'
  ];

  shareholderDisplayedColumns: string[] = [
    'index', 'name', 'percentage', 'category', 'subCategory', 'familyStatus', 'generation'
  ];

  form: FormGroup = this.fb.group({
    founder1: [''],
    founder2: [''],
    founder3: [''],
    founder4: [''],
    founder5: [''],
    yearOfFounding: [''],
    yearWentPublic: [''],
    foundingFamily: [''],
    foundersName: [false],
    familyComments: [''],
    boardRows: this.fb.array([]),
    managementRows: this.fb.array([]),
    executiveRows: this.fb.array([]),
    shareholderRows: this.fb.array([]),
  });

  get boardRowsArray(): FormArray {
    return this.form.get('boardRows') as FormArray;
  }

  get managementRowsArray(): FormArray {
    return this.form.get('managementRows') as FormArray;
  }

  get executiveRowsArray(): FormArray {
    return this.form.get('executiveRows') as FormArray;
  }

  get shareholderRowsArray(): FormArray {
    return this.form.get('shareholderRows') as FormArray;
  }

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  readonly classificationMap: Record<number, string> = {
    1: 'Executive',
    2: 'Non-Executive',
    3: 'Independent',
  };

  getClassificationName(classification: number | null): string {
    if (classification == null) return '';
    return this.classificationMap[classification] ?? '';
  }

  getOccupationName(occupationId: string | null): string {
    if (!occupationId) return '';
    return this.occupations.find(o => o.id === occupationId)?.name ?? '';
  }

  formatPercentage(value: number | null | undefined): string {
    if (value == null) return '0,00';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('family'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'family', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'family', year: this.selectedYear }));
    }

    this.lutService.getFamilyCompanyStatuses().subscribe(data => {
      this.familyStatuses = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.lutService.getFamilyGenerations().subscribe(data => {
      this.familyGenerations = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.lutService.getBoardMemberOccupations().subscribe(data => {
      this.occupations = data;
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  toggleRowHighlight(row: any): void {
    row.highlighted = !row.highlighted;
  }

  onBoardSort(sort: Sort): void {
    this.sortBoardTable(sort, this.boardDataSource, this.boardRowsArray, 'boardRows');
  }

  onManagementSort(sort: Sort): void {
    this.sortBoardTable(sort, this.managementDataSource, this.managementRowsArray, 'managementRows');
  }

  onExecutiveSort(sort: Sort): void {
    this.sortExecutiveTable(sort);
  }

  onShareholderSort(sort: Sort): void {
    this.sortShareholderTable(sort);
  }

  /**
   * The table's current sort, taken from its MatSort header so that rebuilding the
   * rows after a reload (e.g. a save) keeps the column the user picked instead of
   * snapping the data back to name/asc — which left the sort arrow pointing at one
   * column while the rows were ordered by another. Falls back to name/asc before the
   * header has rendered (first load).
   */
  private currentSort(sort: MatSort | undefined): Sort {
    return sort?.active && sort.direction
      ? { active: sort.active, direction: sort.direction }
      : { active: 'name', direction: 'asc' };
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'family', year }));
    this.loadData();
  }

  private loadData(): void {
    if (!this.companyId) return;

    this.spinnerService.show();
    this.familyService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const response = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.isFamilyDetailsCarriedForward = resource.state === 'carried-forward';
        this.isLargeShareholdersCarriedForward = response?.isLargeShareholdersCarriedForward ?? false;
        this.showYearWarning = this.isFamilyDetailsCarriedForward || resource.state === 'absent';

        // Members come from context — populated regardless of envelope state.
        const boardMembers = resource.context?.boardMembers ?? [];
        const executiveMembers = resource.context?.executiveMembers ?? [];
        const boardType = resource.context?.boardType ?? 0;
        this.populateMemberTables(boardMembers, executiveMembers, boardType);

        // Founders and the two year fields belong to CompanyDetails, so they come from
        // context, which is populated in every envelope state. Reading them off `data`
        // left them blank whenever the family resource was absent — the company has no
        // family row and no shareholders yet — even though Details had them filled in.
        this.populateFounderFields(resource.context);

        if (response) {
          this.populateFamilyFields(response);
        } else {
          this.clearFamilyFields();
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetForm();
        } else {
          this.toast.error('Error loading family data');
        }

        console.error('Family data error:', error);
      }
    });
  }

  private populateMemberTables(
    boardMembers: FamilyBoardMemberDto[],
    executiveMembers: FamilyExecutiveMemberDto[],
    boardType: number
  ): void {
    this.companyBoardType = boardType;

    if (boardType !== 0) {
      const supervisory = boardMembers.filter(m => m.boardType === 1);
      const management = boardMembers.filter(m => m.boardType === 2);
      this.boardDataSource.data = supervisory;
      this.managementDataSource.data = management;
      this.buildBoardFormArrays(supervisory, management);
    } else {
      this.boardDataSource.data = boardMembers;
      this.managementDataSource.data = [];
      this.buildBoardFormArrays(boardMembers, []);
    }

    this.executiveDataSource.data = executiveMembers;
    this.form.setControl('executiveRows', this.fb.array(
      executiveMembers.map(m => this.createExecutiveRowGroup(m))
    ));

    this.onBoardSort(this.currentSort(this.boardSort));
    if (boardType !== 0) {
      this.onManagementSort(this.currentSort(this.managementSort));
    }
    this.onExecutiveSort(this.currentSort(this.executiveSort));
  }

  /**
   * Patches the fields the Family tab shares with Details (it edits them, but CompanyDetails
   * is where they live). They are always taken from context so they survive every envelope
   * state; `data` is null for absent/carried-forward years.
   */
  private populateFounderFields(context: FamilyContext | undefined): void {
    this.form.patchValue({
      founder1: context?.founder1 ?? '',
      founder2: context?.founder2 ?? '',
      founder3: context?.founder3 ?? '',
      founder4: context?.founder4 ?? '',
      founder5: context?.founder5 ?? '',
      yearOfFounding: Helpers.toDisplayDate(context?.yearOfFounding),
      yearWentPublic: Helpers.toDisplayDate(context?.yearWentPublic),
    });
  }

  private populateFamilyFields(response: FamilyResponseDto): void {
    this.form.patchValue({
      foundingFamily: response.foundingFamily ?? '',
      foundersName: response.foundersName ?? false,
      familyComments: response.familyComments ?? '',
    });

    const largeShareholders = response.largeShareholders ?? [];
    this.shareholderDataSource.data = largeShareholders;
    this.form.setControl('shareholderRows', this.fb.array(
      largeShareholders.map(m => this.createShareholderRowGroup(m))
    ));
    this.onShareholderSort(this.currentSort(this.shareholderSort));

    this.form.markAsPristine();
  }

  /** Clears only the CompanyFamily-owned fields — the founder/year fields are held by context. */
  private clearFamilyFields(): void {
    this.form.patchValue({
      foundingFamily: '',
      foundersName: false,
      familyComments: '',
    });
    this.shareholderDataSource.data = [];
    this.form.setControl('shareholderRows', this.fb.array([]));
    this.form.markAsPristine();
  }

  private buildBoardFormArrays(boardRows: FamilyBoardMemberDto[], managementRows: FamilyBoardMemberDto[]): void {
    this.form.setControl('boardRows', this.fb.array(
      boardRows.map(m => this.createBoardRowGroup(m))
    ));
    this.form.setControl('managementRows', this.fb.array(
      managementRows.map(m => this.createBoardRowGroup(m))
    ));
  }

  private createBoardRowGroup(member: FamilyBoardMemberDto): FormGroup {
    return this.fb.group({
      detailsId: [member.detailsId],
      boardMemberId: [member.boardMemberId],
      directorId: [member.directorId],
      familyCompanyStatusId: [member.familyCompanyStatusId ?? null],
      familyGenerationId: [member.familyGenerationId ?? null],
    });
  }

  private createExecutiveRowGroup(member: FamilyExecutiveMemberDto): FormGroup {
    return this.fb.group({
      detailsId: [member.detailsId],
      executiveMemberId: [member.executiveMemberId],
      directorId: [member.directorId],
      familyCompanyStatusId: [member.familyCompanyStatusId ?? null],
      familyGenerationId: [member.familyGenerationId ?? null],
    });
  }

  private createShareholderRowGroup(member: FamilyLargeShareholderDto): FormGroup {
    return this.fb.group({
      largeShareholderId: [member.largeShareholderId],
      familyCompanyStatusId: [member.familyCompanyStatusId ?? null],
      familyGenerationId: [member.familyGenerationId ?? null],
    });
  }

  private sortBoardTable(sort: Sort, dataSource: MatTableDataSource<FamilyBoardMemberDto>, formArray: FormArray, formArrayName: string): void {
    if (!sort.active || sort.direction === '') return;

    const dir = sort.direction === 'asc' ? 1 : -1;
    const paired = dataSource.data.map((row, i) => {
      const group = formArray.at(i) as FormGroup;
      return { merged: { ...row, ...group.value }, group };
    });

    paired.sort((pa, pb) => {
      const a = pa.merged;
      const b = pb.merged;
      let valA: any;
      let valB: any;

      switch (sort.active) {
        case 'name':
          valA = a.directorFullName ?? '';
          valB = b.directorFullName ?? '';
          return dir * valA.localeCompare(valB);
        case 'percentage':
          valA = a.firstClassSharesPercentage ?? -Infinity;
          valB = b.firstClassSharesPercentage ?? -Infinity;
          return dir * ((Number(valA) || 0) - (Number(valB) || 0));
        case 'classification':
          valA = a.classification ?? 99;
          valB = b.classification ?? 99;
          return dir * (valA - valB);
        case 'occupation':
          valA = this.getOccupationName(a.occupationId);
          valB = this.getOccupationName(b.occupationId);
          return dir * valA.localeCompare(valB);
        case 'familyStatus':
          valA = this.familyStatuses.find(s => s.id === a.familyCompanyStatusId)?.name ?? '';
          valB = this.familyStatuses.find(s => s.id === b.familyCompanyStatusId)?.name ?? '';
          return dir * valA.localeCompare(valB);
        case 'generation':
          valA = this.familyGenerations.find(g => g.id === a.familyGenerationId)?.name ?? '';
          valB = this.familyGenerations.find(g => g.id === b.familyGenerationId)?.name ?? '';
          return dir * valA.localeCompare(valB);
        default:
          return 0;
      }
    });

    // Reuse the existing FormGroup instances so per-row dirty state, values, and
    // valueChanges subscriptions survive the sort. Recreating the groups would reset
    // their dirty flags, and performSave() only sends rows whose group is dirty — so
    // any edit made before a sort would be silently dropped on save.
    dataSource.data = paired.map(p => p.merged);
    this.form.setControl(formArrayName, this.fb.array(paired.map(p => p.group)));
  }

  private sortExecutiveTable(sort: Sort): void {
    if (!sort.active || sort.direction === '') return;

    const dir = sort.direction === 'asc' ? 1 : -1;
    const paired = this.executiveDataSource.data.map((row, i) => {
      const group = this.executiveRowsArray.at(i) as FormGroup;
      return { merged: { ...row, ...group.value }, group };
    });

    paired.sort((pa, pb) => {
      const a = pa.merged;
      const b = pb.merged;
      let valA: any;
      let valB: any;

      switch (sort.active) {
        case 'name':
          valA = a.directorFullName ?? '';
          valB = b.directorFullName ?? '';
          return dir * valA.localeCompare(valB);
        case 'percentage':
          valA = a.firstClassSharesPercentage ?? -Infinity;
          valB = b.firstClassSharesPercentage ?? -Infinity;
          return dir * ((Number(valA) || 0) - (Number(valB) || 0));
        case 'occupation':
          valA = this.getOccupationName(a.occupationId);
          valB = this.getOccupationName(b.occupationId);
          return dir * valA.localeCompare(valB);
        case 'familyStatus':
          valA = this.familyStatuses.find(s => s.id === a.familyCompanyStatusId)?.name ?? '';
          valB = this.familyStatuses.find(s => s.id === b.familyCompanyStatusId)?.name ?? '';
          return dir * valA.localeCompare(valB);
        case 'generation':
          valA = this.familyGenerations.find(g => g.id === a.familyGenerationId)?.name ?? '';
          valB = this.familyGenerations.find(g => g.id === b.familyGenerationId)?.name ?? '';
          return dir * valA.localeCompare(valB);
        default:
          return 0;
      }
    });

    // Reuse the existing FormGroup instances so per-row dirty state, values, and
    // valueChanges subscriptions survive the sort. Recreating the groups would reset
    // their dirty flags, and performSave() only sends rows whose group is dirty — so
    // any edit made before a sort would be silently dropped on save.
    this.executiveDataSource.data = paired.map(p => p.merged);
    this.form.setControl('executiveRows', this.fb.array(paired.map(p => p.group)));
  }

  private sortShareholderTable(sort: Sort): void {
    if (!sort.active || sort.direction === '') return;

    const dir = sort.direction === 'asc' ? 1 : -1;
    const paired = this.shareholderDataSource.data.map((row, i) => {
      const group = this.shareholderRowsArray.at(i) as FormGroup;
      return { merged: { ...row, ...group.value }, group };
    });

    paired.sort((pa, pb) => {
      const a = pa.merged;
      const b = pb.merged;
      let valA: any;
      let valB: any;

      switch (sort.active) {
        case 'name':
          valA = a.largeShareholderName ?? '';
          valB = b.largeShareholderName ?? '';
          return dir * valA.localeCompare(valB);
        case 'percentage':
          valA = a.firstClassSharesPercentage ?? -Infinity;
          valB = b.firstClassSharesPercentage ?? -Infinity;
          return dir * ((Number(valA) || 0) - (Number(valB) || 0));
        case 'category':
          valA = a.categoryName ?? '';
          valB = b.categoryName ?? '';
          return dir * valA.localeCompare(valB);
        case 'subCategory':
          valA = a.subCategoryName ?? '';
          valB = b.subCategoryName ?? '';
          return dir * valA.localeCompare(valB);
        case 'familyStatus':
          valA = this.familyStatuses.find(s => s.id === a.familyCompanyStatusId)?.name ?? '';
          valB = this.familyStatuses.find(s => s.id === b.familyCompanyStatusId)?.name ?? '';
          return dir * valA.localeCompare(valB);
        case 'generation':
          valA = this.familyGenerations.find(g => g.id === a.familyGenerationId)?.name ?? '';
          valB = this.familyGenerations.find(g => g.id === b.familyGenerationId)?.name ?? '';
          return dir * valA.localeCompare(valB);
        default:
          return 0;
      }
    });

    // Reuse the existing FormGroup instances so per-row dirty state, values, and
    // valueChanges subscriptions survive the sort. Recreating the groups would reset
    // their dirty flags, and performSave() only sends rows whose group is dirty — so
    // any edit made before a sort would be silently dropped on save.
    this.shareholderDataSource.data = paired.map(p => p.merged);
    this.form.setControl('shareholderRows', this.fb.array(paired.map(p => p.group)));
  }

  private resetForm(): void {
    this.boardDataSource.data = [];
    this.managementDataSource.data = [];
    this.executiveDataSource.data = [];
    this.shareholderDataSource.data = [];
    this.form.setControl('boardRows', this.fb.array([]));
    this.form.setControl('managementRows', this.fb.array([]));
    this.form.setControl('executiveRows', this.fb.array([]));
    this.form.setControl('shareholderRows', this.fb.array([]));
    this.form.patchValue({
      founder1: '', founder2: '', founder3: '', founder4: '', founder5: '',
      yearOfFounding: '', yearWentPublic: '', foundingFamily: '', foundersName: false, familyComments: '',
    });
    this.form.markAsPristine();
  }

  private performSave(): void {
    if (!this.companyId) return;

    const formVal = this.form.value;

    const boardMembers: FamilyBoardMemberUpsertDto[] = [];
    this.boardRowsArray.controls.forEach(group => {
      if (group.dirty) {
        boardMembers.push({
          detailsId: group.get('detailsId')?.value || undefined,
          boardMemberId: group.get('boardMemberId')?.value,
          directorId: group.get('directorId')?.value,
          familyCompanyStatusId: group.get('familyCompanyStatusId')?.value,
          familyGenerationId: group.get('familyGenerationId')?.value,
        });
      }
    });
    this.managementRowsArray.controls.forEach(group => {
      if (group.dirty) {
        boardMembers.push({
          detailsId: group.get('detailsId')?.value || undefined,
          boardMemberId: group.get('boardMemberId')?.value,
          directorId: group.get('directorId')?.value,
          familyCompanyStatusId: group.get('familyCompanyStatusId')?.value,
          familyGenerationId: group.get('familyGenerationId')?.value,
        });
      }
    });

    const executiveMembers: FamilyExecutiveMemberUpsertDto[] = [];
    this.executiveRowsArray.controls.forEach(group => {
      if (group.dirty) {
        executiveMembers.push({
          detailsId: group.get('detailsId')?.value || undefined,
          executiveMemberId: group.get('executiveMemberId')?.value,
          directorId: group.get('directorId')?.value,
          familyCompanyStatusId: group.get('familyCompanyStatusId')?.value,
          familyGenerationId: group.get('familyGenerationId')?.value,
        });
      }
    });

    const largeShareholders: FamilyLargeShareholderUpsertDto[] = [];
    this.shareholderRowsArray.controls.forEach(group => {
      if (group.dirty || this.isLargeShareholdersCarriedForward) {
        largeShareholders.push({
          largeShareholderId: group.get('largeShareholderId')?.value,
          familyCompanyStatusId: group.get('familyCompanyStatusId')?.value,
          familyGenerationId: group.get('familyGenerationId')?.value,
        });
      }
    });

    const founderFields = [
      'founder1', 'founder2', 'founder3', 'founder4', 'founder5', 'yearOfFounding', 'yearWentPublic'
    ];
    const topLevelFields = [...founderFields, 'foundingFamily', 'foundersName', 'familyComments'];
    const topLevelDirty = topLevelFields.some(field => this.form.get(field)?.dirty);

    if (boardMembers.length === 0 && executiveMembers.length === 0 && largeShareholders.length === 0 && !topLevelDirty) {
      this.toast.info('No changes to save');
      return;
    }

    const request: FamilyUpsertRequest = {
      year: parseInt(this.selectedYear),
      // Sent only when the user edited one of them here, and then a blank one means they cleared it.
      // Founders are written to every year of the company, so a save made for anything else must
      // not send back what this tab loaded: the Details tab may have changed them since.
      founderFieldsIncluded: founderFields.some(field => this.form.get(field)?.dirty),
      founder1: formVal.founder1 || undefined,
      founder2: formVal.founder2 || undefined,
      founder3: formVal.founder3 || undefined,
      founder4: formVal.founder4 || undefined,
      founder5: formVal.founder5 || undefined,
      yearOfFounding: Helpers.toIsoDate(formVal.yearOfFounding) || undefined,
      yearWentPublic: Helpers.toIsoDate(formVal.yearWentPublic) || undefined,
      foundingFamily: formVal.foundingFamily || undefined,
      foundersName: formVal.foundersName ?? false,
      familyComments: formVal.familyComments || undefined,
      boardMembers,
      executiveMembers,
      largeShareholders,
    };

    this.spinnerService.show();
    this.familyService.upsert(this.companyId!, request).subscribe({
      next: (result) => {
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success('Family data saved successfully');
        // The server refuses rows for members the year's roster no longer holds — someone was
        // removed on the Board tab while these tables were still showing the old roster.
        if (membersWereDropped(result)) {
          this.toast.warning(MEMBERS_DROPPED_MESSAGE);
        }
        this.loadData();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to save family data');
        console.error('Save error:', err);
      }
    });
  }

  onDateFocus(event: Event): void {
    Helpers.onDateInputFocus(event);
  }

  onDateBlur(event: Event): void {
    Helpers.onDateInputBlur(event);
  }

  onYearDateInput(event: Event, controlName: string): void {
    const value = Helpers.onDateInputFormat(event);
    this.form.get(controlName)?.setValue(value, { emitEvent: false });
    this.form.get(controlName)?.markAsDirty();
  }
}
